import React from 'react';
import { CheckCircle, AlertCircle, Download, Zap, FileSpreadsheet } from 'lucide-react';
import { ExportXlsxResponse, JobStatus } from '../types';

interface ProcessingStatusProps {
  isProcessing: boolean;
  progress: number;
  error: string | null;
  exportResult: ExportXlsxResponse | null;
  onDownload: () => void;
  jobStatus?: JobStatus | null;
  filesCount?: number;
  processedFilesCount?: number;
}

const ProcessingStatus: React.FC<ProcessingStatusProps> = ({
  isProcessing,
  progress,
  error,
  exportResult,
  onDownload,
  jobStatus,
  filesCount = 0,
  processedFilesCount = 0,
}) => {
  if (!isProcessing && !error && !exportResult) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Processing Status</h2>
      
      {/* Processing State */}
      {isProcessing && (
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
              <Zap className="h-4 w-4 text-blue-600 animate-pulse" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Processing your files...</p>
              {jobStatus === 'IN_PROGRESS' && filesCount > 0 ? (
                <p className="text-xs text-gray-500">
                  Processing {processedFilesCount} of {filesCount} files ({Math.round((processedFilesCount / filesCount) * 100)}%)
                </p>
              ) : (
                <p className="text-xs text-gray-500">Extracting data and generating Excel file</p>
              )}
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 text-center">
            {jobStatus === 'IN_PROGRESS' && filesCount > 0 
              ? `${processedFilesCount} / ${filesCount} files processed (${progress}%)`
              : `${progress}% complete`}
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-start space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full flex-shrink-0">
            <AlertCircle className="h-4 w-4 text-red-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900">Processing Failed</p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Success State */}
      {exportResult && (
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
              <CheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900">Processing Complete!</p>
              <p className="text-xs text-green-600">
                Successfully processed {exportResult.filesProcessed} file(s)
              </p>
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-900">Excel file ready for download</p>
                <p className="text-xs text-green-600">{exportResult.message}</p>
              </div>
            </div>
            
            <button
              onClick={onDownload}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Excel File
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessingStatus;
